import { useKV } from '@/hooks/useLocalStorage';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CaretRight, Lightbulb, Target, X } from '@phosphor-icons/react';

interface HelpHint {
  id: string;
  title: string;
  content: string;
  tab?: string;
  persistent?: boolean;
}

interface HelpHintsProps {
  currentTab: string;
  selectedDatasetCount: number;
  hasQualityReports: boolean;
  hasCorrelations: boolean;
}

const helpHints: HelpHint[] = [
  {
    id: 'templates-hint',
    title: 'Start with Templates',
    content:
      'New to data science? Templates filter datasets by research domain and guide you to relevant data sources.',
    tab: 'templates',
    persistent: true,
  },
  {
    id: 'first-dataset',
    title: 'Select Your First Dataset',
    content:
      'Click "Explore" on any data source to see available datasets. Look for high quality scores (80%+) to start.',
    tab: 'discover',
  },
  {
    id: 'dataset-selection',
    title: 'Browse Available Datasets',
    content:
      "Review each dataset's details, quality score, and sample data. Select datasets that align with your research questions.",
    tab: 'datasets',
  },
  {
    id: 'multiple-datasets',
    title: 'Add More Datasets',
    content:
      'Select 2-3 datasets to unlock correlation analysis. Choose datasets from similar time periods or regions.',
    tab: 'discover',
  },
  {
    id: 'quality-check',
    title: 'Check Data Quality',
    content:
      "Review quality reports to understand your data's reliability before drawing conclusions.",
    tab: 'quality',
  },
  {
    id: 'correlations-ready',
    title: 'Ready for Analysis',
    content:
      'With multiple datasets selected, you can now analyze correlations and find meaningful patterns.',
    tab: 'correlations',
  },
  {
    id: 'transform-pipeline',
    title: 'Transform Your Data',
    content:
      'Build interactive transformation pipelines to clean, filter, and prepare your data for analysis.',
    tab: 'transform',
  },
  {
    id: 'visualization-tip',
    title: 'Visualize Your Data',
    content:
      'Charts help you spot trends and communicate insights. Start with simple bar or line charts.',
    tab: 'visualize',
  },
  {
    id: 'export-data',
    title: 'Export Your Results',
    content:
      'Export datasets in multiple formats including CSV, JSON, Excel, and ready-to-run R scripts or Jupyter notebooks for further analysis.',
    tab: 'export',
  },
];

export function HelpHints({
  currentTab,
  selectedDatasetCount,
  hasQualityReports,
  hasCorrelations,
}: Readonly<HelpHintsProps>) {
  const [dismissedHints, setDismissedHints] = useKV<string[]>(
    'dismissed-hints',
    []
  );

  const getRelevantHint = (): HelpHint | null => {
    // Determine which hint to show based on user progress
    if (currentTab === 'templates') {
      return helpHints.find(h => h.id === 'templates-hint') || null;
    }

    if (currentTab === 'discover') {
      if (selectedDatasetCount === 0) {
        return helpHints.find(h => h.id === 'first-dataset') || null;
      } else if (selectedDatasetCount === 1) {
        return helpHints.find(h => h.id === 'multiple-datasets') || null;
      }
    }

    if (
      currentTab === 'quality' &&
      selectedDatasetCount > 0 &&
      !hasQualityReports
    ) {
      return helpHints.find(h => h.id === 'quality-check') || null;
    }

    if (
      currentTab === 'correlations' &&
      selectedDatasetCount >= 2 &&
      !hasCorrelations
    ) {
      return helpHints.find(h => h.id === 'correlations-ready') || null;
    }

    if (currentTab === 'transform' && selectedDatasetCount > 0) {
      return helpHints.find(h => h.id === 'transform-pipeline') || null;
    }

    if (currentTab === 'visualize' && selectedDatasetCount > 0) {
      return helpHints.find(h => h.id === 'visualization-tip') || null;
    }

    if (currentTab === 'export' && selectedDatasetCount > 0) {
      return helpHints.find(h => h.id === 'export-data') || null;
    }

    return null;
  };

  const relevantHint = getRelevantHint();

  if (!relevantHint || dismissedHints.includes(relevantHint.id)) {
    return null;
  }

  const handleDismiss = () => {
    setDismissedHints(prev => [...prev, relevantHint.id]);
  };

  return (
    <Card className='border-blue-200 bg-blue-50/30 mb-6'>
      <CardContent className='p-4'>
        <div className='flex items-start gap-3'>
          <div className='p-1.5 rounded-full bg-blue-100 flex-shrink-0'>
            <Lightbulb className='w-4 h-4 text-blue-600' />
          </div>
          <div className='flex-1 min-w-0'>
            <h4 className='font-medium text-blue-800 mb-1'>
              {relevantHint.title}
            </h4>
            <p className='text-sm text-blue-700 leading-relaxed'>
              {relevantHint.content}
            </p>
          </div>
          <Button
            variant='ghost'
            size='sm'
            onClick={handleDismiss}
            className='h-6 w-6 p-0 text-blue-400 hover:text-blue-600 flex-shrink-0'
          >
            <X className='w-3 h-3' />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Quick action tips that appear in specific contexts
interface QuickTipProps {
  type: 'correlation' | 'visualization' | 'quality';
  onAction: () => void;
}

export function QuickTip({ type, onAction }: Readonly<QuickTipProps>) {
  const tips = {
    correlation: {
      title: 'Analyze Correlations',
      description:
        'You have 2+ datasets selected. Find patterns and relationships between them.',
      actionText: 'Analyze Now',
      icon: <CaretRight className='w-4 h-4' />,
    },
    visualization: {
      title: 'Create Visualization',
      description:
        'Turn your data into charts and graphs to spot trends and insights.',
      actionText: 'Visualize',
      icon: <CaretRight className='w-4 h-4' />,
    },
    quality: {
      title: 'Check Data Quality',
      description:
        "Review quality reports to understand your data's reliability.",
      actionText: 'View Reports',
      icon: <CaretRight className='w-4 h-4' />,
    },
  };

  const tip = tips[type];

  return (
    <Card className='border-accent/20 bg-accent/5'>
      <CardContent className='p-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <Target className='w-5 h-5 text-accent flex-shrink-0' />
            <div>
              <h4 className='font-medium text-accent-foreground'>
                {tip.title}
              </h4>
              <p className='text-sm text-accent-foreground/80'>
                {tip.description}
              </p>
            </div>
          </div>
          <Button onClick={onAction} size='sm' className='gap-1 ml-4'>
            {tip.actionText}
            {tip.icon}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
